import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Button, Switch, Popover, Input, Tag, Space } from "antd";
import { Check, X, Trash2, Tag as TagIcon, Palette } from "lucide-react";
import { v4 as uuid } from "uuid";

import { useAutocompleteSuggestions } from "../hooks/useTagSuggestions.ts";



import Checklist, { Item } from "../components/Checklist.tsx";
import LabelSelectorModal from "../components/LabelSelectorModal.tsx";

import type { Note } from "../types/note.ts";
import type { Label } from "../api/labelApi.ts";           // ← интерфейс {id,name,color?}
import { lightenColor, hexToArgb, argbToHex, pastelColors, DEFAULT_COLOR } from "../utils/colors.ts";
import axios from "axios";
import { getUserId } from "../utils/getUserId.ts";



/* ─────────────── Типы ─────────────── */
export interface EditorProps {
  note: Note; // note.labels?: Label[]
  onSave: (n: Note) => void;
  onCancel: () => void;
  onDelete: () => void;
}

export interface NoteEditorHandle {
  isDirty: () => boolean;
}



/* ───────────────────────────────────── */
const NoteEditor = forwardRef<NoteEditorHandle, EditorProps>(
  ({ note, onSave, onCancel, onDelete }, ref) => {
    /* ---------- state ---------- */
const userId = note.user_id ?? getUserId();


const [body, setBody] = useState(note.body ?? ""); // ← сначала объявляем


const { suggestions, trigger } = useAutocompleteSuggestions(body, userId);

    const [color, setColor] = useState(note.color ?? DEFAULT_COLOR);

    const [isList, setIsList] = useState(note.is_list);

    const [title, setTitle] = useState(note.title ?? "");  // ← default ''


const [allTags, setAllTags] = useState<string[]>([]);
// хук уже всё сам делает






const handleSuggestionClick = (text: string) => {
  const match =
    trigger === "#"
      ? body.match(/(^|\s)#([\p{L}\p{N}_]{0,20})$/u)
      : body.match(/(^|\s)@([\p{L}\p{N}_ ]{0,40})$/u);

  if (!match) return;
  const lastIndex = body.lastIndexOf(match[0]);
  const newText =
    body.substring(0, lastIndex) + `${match[1]}${trigger}${text} `;
  setBody(newText);
};

    
    const [items, setItems] = useState<Item[]>(
  (note.items ?? []).length                     // ← safe default []
    ? note.items!.map((it) => ({
            id: uuid(),
            text: it.itemText,
            checked: it.checked,
          }))
        : []
    );


    const norm = (s?: string | null) => (s ?? "").trim();

const isDirty = () =>
  norm(title) !== norm(note.title) ||
  norm(body)  !== norm(note.body)  ||
  color       !== note.color       ||
  isList      !== note.is_list     ||
  JSON.stringify(items)  !== JSON.stringify(
        (note.items ?? []).map((x) => ({ text: x.itemText, checked: x.checked }))
      ) ||
  JSON.stringify(labels) !== JSON.stringify(note.labels ?? []);

    const [labels, setLabels] = useState<Label[]>(note.labels ?? []);
    const [showLabels, setShowLabels] = useState(false);


    /* ---------- сохранить ---------- */
const handleSave = () => {
  const prepared: Note = {
    ...note,
    title,
    body: isList ? "" : body,
    color,
    is_list: isList,
    items: isList
      ? items.map((it, i) => ({
          id: 0,
          itemText: it.text,
          checked: it.checked,
          position: i,
        }))
      : [],
    labels: labels.map((l) => ({
      id: l.id,
      name: l.name,
      color: l.color ?? undefined, // ← критически важно
    })),
    user_id: note.user_id,
  };
  onSave(prepared);
};

    /* ---------- переключение Text/List ---------- */
    const toggleList = (v: boolean) => {
      if (v === isList) return;
      if (v) {
        // текст → список
        const lines = body
          .split("\n")
          .map((l) => l.trim())
          .filter(Boolean);
        setItems(
          lines.length
            ? lines.map((t) => ({ id: uuid(), text: t, checked: false }))
            : [{ id: uuid(), text: "", checked: false }]
        );
        setBody("");
      } else {
        // список → текст
        setBody(items.map((it) => it.text).join("\n"));
        setItems([]);
      }
      setIsList(v);
    };

    /* ---------- Memo chips ---------- */
    const labelChips = useMemo(
      () =>
        labels.map((l) => (
          <Tag
            key={l.id}
            color={l.color ?? "orange"}
            closable
            onClose={() =>
              setLabels((prev) => prev.filter((x) => x.id !== l.id))
            }
            style={{ userSelect: "none" }}
          >
            {l.name}
          </Tag>
        )),
      [labels]
    );

    /* ---------- UI ---------- */
    return (
      <div
        className="editor-wrapper"
        style={{
          background: lightenColor(color, 0.9),
          borderRadius: 12,
          padding: 24,
          maxWidth: 720,
          margin: "0 auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          className="editor-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Input
            value={title}
            placeholder="Заголовок"
            onChange={(e) => setTitle(e.target.value)}
            bordered={false}
            style={{ fontSize: 20, fontWeight: 600 }}
          />

          <Space>
           <Popover
  trigger="click"
  content={
    <>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        {pastelColors.map((c) => (
          <div
            key={c}
            onClick={() => setColor(c)}
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: argbToHex(c),
              border: color === c ? "2px solid #000" : "1px solid #ccc",
              cursor: "pointer",
            }}
          />
        ))}
      </div>
      </>
  }
>
  <Button type="text" icon={<Palette size={16} />} />
</Popover>


            <Button
              type="text"
              icon={<TagIcon size={16} />}
              onClick={() => setShowLabels(true)}
            />
          </Space>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", marginTop: 12 }}>
          {isList ? (
            <Checklist items={items} onChange={setItems} />
          ) : (
            <Input.TextArea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              autoSize={{ minRows: 12 }}
              placeholder="Текст…"
              style={{ background: "transparent" }}
            />
          )}
        </div>

{!isList && suggestions.length > 0 && (
  <div className="hashtag-suggestions">
    {suggestions.map((text) => (
      <div
        key={text}
        className="hashtag-suggestion"
        onClick={() => handleSuggestionClick(text)}
      >
        {trigger}{text}
      </div>
    ))}
  </div>
)}


        {/* Chips */}
        {labels.length > 0 && <div style={{ marginTop: 8 }}>{labelChips}</div>}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 16,
          }}
        >
          <Switch
            checked={isList}
            onChange={toggleList}
            checkedChildren="Список"
            unCheckedChildren="Текст"
          />

          <Space>
            <Button icon={<Check size={16} />} onClick={handleSave}>
              Сохранить
            </Button>
            <Button icon={<X size={16} />} onClick={onCancel}>
              Отмена
            </Button>
            {note.id !== 0 && (
              <Button
                danger
                type="text"
                icon={<Trash2 size={16} />}
                onClick={onDelete}
              />
            )}
          </Space>
        </div>

        <LabelSelectorModal
          open={showLabels}
          initial={labels}
          onClose={(sel) => {
            if (sel) setLabels(sel);
            setShowLabels(false);
          }}
        />
      </div>
    );
  }
);

export default NoteEditor;
